import { Book } from '../database.types';
import { ExportResult } from './types';
import { markdownToHtml } from './markdown';
import { prepareChapters } from './markdown-parser';
import JSZip from 'jszip';

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

const bookCss = `body {
  font-family: Georgia, "Times New Roman", serif;
  line-height: 1.4;
  margin: 5%;
}
h1 {
  font-size: 1.8em;
  font-weight: bold;
  text-align: center;
  margin: 1.5em 0 0.5em;
  page-break-before: always;
}
h2 {
  font-size: 1.4em;
  font-weight: bold;
  margin: 1em 0 0.5em;
}
h3 {
  font-size: 1.2em;
  font-weight: bold;
  margin: 0.8em 0 0.4em;
}
p {
  text-align: justify;
  margin: 0.4em 0;
  text-indent: 1.5em;
}
h1 + p, h2 + p, h3 + p {
  text-indent: 0;
}
ul, ol {
  margin: 0.5em 0 0.5em 2em;
}
li {
  margin: 0.2em 0;
}
blockquote {
  font-style: italic;
  margin: 1em 2em;
  padding-left: 1em;
  border-left: 3px solid #ccc;
}
strong {
  font-weight: bold;
}
em {
  font-style: italic;
}
.cover-page {
  text-align: center;
  margin-top: 30%;
}
.cover-page h1 {
  font-size: 2.2em;
  margin-bottom: 0.5em;
  page-break-before: auto;
}
.cover-page .subtitle {
  font-size: 1.4em;
  font-style: italic;
  margin-bottom: 1em;
}
.cover-page .author {
  font-size: 1.2em;
  margin-top: 2em;
}
.copyright-page {
  margin-top: 40%;
  font-size: 0.9em;
  text-align: center;
}
.about-author h1 {
  margin-top: 2em;
}`;

export async function generateEpub(book: Book): Promise<ExportResult> {
  const zip = new JSZip();
  const bookId = crypto.randomUUID();

  zip.file('mimetype', 'application/epub+zip');

  const metaInf = zip.folder('META-INF');
  metaInf?.file('container.xml', `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`);

  const oebps = zip.folder('OEBPS');
  const chapters = prepareChapters(book.chapters || []);

  oebps?.file('styles.css', bookCss);

  // Cover page
  const coverHtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>${escapeXml(book.title)}</title>
    <link rel="stylesheet" type="text/css" href="styles.css"/>
  </head>
  <body>
    <div class="cover-page">
      <h1>${escapeXml(book.title)}</h1>
      ${book.subtitle ? `<p class="subtitle">${escapeXml(book.subtitle)}</p>` : ''}
      ${book.author ? `<p class="author">by ${escapeXml(book.author)}</p>` : ''}
    </div>
  </body>
</html>`;
  oebps?.file('cover.xhtml', coverHtml);

  // Copyright page
  const copyrightHtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>Copyright</title>
    <link rel="stylesheet" type="text/css" href="styles.css"/>
  </head>
  <body>
    <div class="copyright-page">
      <p>Copyright &#169; ${new Date().getFullYear()} ${escapeXml(book.author || 'All rights reserved')}</p>
      <p>All rights reserved.</p>
      <p>This book or any portion thereof may not be reproduced or used in any manner whatsoever without the express written permission of the publisher except for the use of brief quotations in a book review.</p>
    </div>
  </body>
</html>`;
  oebps?.file('copyright.xhtml', copyrightHtml);

  // Chapter files
  chapters.forEach((chapter, index) => {
    const html = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>${escapeXml(chapter.chapter_title)}</title>
    <link rel="stylesheet" type="text/css" href="styles.css"/>
  </head>
  <body>
    <h1>${escapeXml(chapter.chapter_title)}</h1>
    ${markdownToHtml(chapter.generated_text || '')}
  </body>
</html>`;
    oebps?.file(`chapter${index + 1}.xhtml`, html);
  });

  // References/Notes page — collect all chapter references
  const chaptersWithRefs = chapters.filter(ch => ch.chapter_references && ch.chapter_references.trim());
  const hasReferences = chaptersWithRefs.length > 0;
  if (hasReferences) {
    let refsBody = '<h1>Notes</h1>\n';
    for (const chapter of chaptersWithRefs) {
      refsBody += `<h2>${escapeXml(chapter.chapter_title)}</h2>\n<ol>\n`;
      const refs = chapter.chapter_references!.split('\n').filter((r: string) => r.trim());
      for (const ref of refs) {
        refsBody += `  <li>${escapeXml(ref.trim())}</li>\n`;
      }
      refsBody += '</ol>\n';
    }

    const refsHtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>Notes</title>
    <link rel="stylesheet" type="text/css" href="styles.css"/>
  </head>
  <body>
    ${refsBody}
  </body>
</html>`;
    oebps?.file('references.xhtml', refsHtml);
  }

  // Author bio page
  const hasAuthorBio = !!book.author_bio;
  if (hasAuthorBio) {
    const bioHtml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml">
  <head>
    <title>About the Author</title>
    <link rel="stylesheet" type="text/css" href="styles.css"/>
  </head>
  <body>
    <div class="about-author">
      <h1>About the Author</h1>
      ${markdownToHtml(book.author_bio || '')}
    </div>
  </body>
</html>`;
    oebps?.file('author-bio.xhtml', bioHtml);
  }

  // TOC
  const toc = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:epub="http://www.idpf.org/2007/ops">
  <head>
    <title>Table of Contents</title>
    <link rel="stylesheet" type="text/css" href="styles.css"/>
  </head>
  <body>
    <nav epub:type="toc" id="toc">
      <h1>Table of Contents</h1>
      <ol>
        ${chapters.map((chapter, i) =>
          `<li><a href="chapter${i + 1}.xhtml">${escapeXml(chapter.chapter_title)}</a></li>`
        ).join('\n        ')}
      </ol>
    </nav>
  </body>
</html>`;
  oebps?.file('toc.xhtml', toc);

  // Spine order: Cover -> Copyright -> TOC -> Chapters -> Author Bio
  const manifestItems = [
    `<item id="css" href="styles.css" media-type="text/css"/>`,
    `<item id="cover" href="cover.xhtml" media-type="application/xhtml+xml"/>`,
    `<item id="copyright" href="copyright.xhtml" media-type="application/xhtml+xml"/>`,
    `<item id="toc" href="toc.xhtml" media-type="application/xhtml+xml" properties="nav"/>`,
    ...chapters.map((_, i) => `<item id="chapter${i + 1}" href="chapter${i + 1}.xhtml" media-type="application/xhtml+xml"/>`),
    ...(hasReferences ? [`<item id="references" href="references.xhtml" media-type="application/xhtml+xml"/>`] : []),
    ...(hasAuthorBio ? [`<item id="author-bio" href="author-bio.xhtml" media-type="application/xhtml+xml"/>`] : []),
  ];

  const spineItems = [
    `<itemref idref="cover"/>`,
    `<itemref idref="copyright"/>`,
    `<itemref idref="toc"/>`,
    ...chapters.map((_, i) => `<itemref idref="chapter${i + 1}"/>`),
    ...(hasReferences ? [`<itemref idref="references"/>`] : []),
    ...(hasAuthorBio ? [`<itemref idref="author-bio"/>`] : []),
  ];

  const contentOpf = `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" version="3.0" unique-identifier="BookId">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:title>${escapeXml(book.title)}</dc:title>
    <dc:creator>${escapeXml(book.author || 'Anonymous')}</dc:creator>
    <dc:language>en</dc:language>
    <dc:identifier id="BookId">urn:uuid:${bookId}</dc:identifier>
    <meta property="dcterms:modified">${new Date().toISOString()}</meta>
  </metadata>
  <manifest>
    ${manifestItems.join('\n    ')}
  </manifest>
  <spine>
    ${spineItems.join('\n    ')}
  </spine>
</package>`;

  oebps?.file('content.opf', contentOpf);

  // Validate EPUB structure before returning
  // Recommendation: authors should run EPUBCheck (epubcheck.org) before uploading to KDP
  validateEpubStructure(zip, chapters, hasReferences, hasAuthorBio, book);

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/epub+zip',
    compression: 'DEFLATE',
    compressionOptions: { level: 9 }
  });

  return {
    filename: `${book.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.epub`,
    blob,
    mimeType: 'application/epub+zip'
  };
}

/**
 * Validates EPUB structure to catch common issues before export.
 * Logs warnings but does NOT block the export.
 */
function validateEpubStructure(
  zip: JSZip,
  chapters: ReturnType<typeof prepareChapters>,
  hasReferences: boolean,
  hasAuthorBio: boolean,
  book: { title: string }
): void {
  const warnings: string[] = [];
  const oebps = zip.folder('OEBPS');

  // Check all chapter files exist in the zip
  chapters.forEach((_, i) => {
    const filename = `chapter${i + 1}.xhtml`;
    if (!oebps?.file(filename)) {
      warnings.push(`Missing chapter file: ${filename}`);
    }
  });

  // Check required structural files
  const requiredFiles = ['cover.xhtml', 'copyright.xhtml', 'toc.xhtml', 'styles.css', 'content.opf'];
  for (const file of requiredFiles) {
    if (!oebps?.file(file)) {
      warnings.push(`Missing required file: ${file}`);
    }
  }

  // Check references file if expected
  if (hasReferences && !oebps?.file('references.xhtml')) {
    warnings.push('Missing references.xhtml despite chapters having references');
  }

  // Check author bio file if expected
  if (hasAuthorBio && !oebps?.file('author-bio.xhtml')) {
    warnings.push('Missing author-bio.xhtml despite having author bio');
  }

  // Check META-INF/container.xml
  if (!zip.folder('META-INF')?.file('container.xml')) {
    warnings.push('Missing META-INF/container.xml');
  }

  // Check mimetype
  if (!zip.file('mimetype')) {
    warnings.push('Missing mimetype file');
  }

  // Validate metadata presence (title is required)
  if (!book.title) {
    warnings.push('Missing required metadata: title');
  }

  if (warnings.length > 0) {
    console.warn('[EPUB Validation] Warnings found:');
    warnings.forEach(w => console.warn(`  - ${w}`));
    console.warn('[EPUB Validation] We recommend validating with EPUBCheck (epubcheck.org) before uploading to KDP.');
  } else {
    console.log('[EPUB Validation] Structure check passed.');
  }
}
